<template>
    <div class="chat-widget">
        <!-- Bubble button -->
        <button class="chat-widget__bubble" @click="toggle" :aria-label="open ? 'Close chat' : 'Open chat'">
            <svg v-if="!open" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            <span v-if="!open && unread > 0" class="chat-widget__badge">{{ unread }}</span>
        </button>

        <!-- Chat panel -->
        <div v-show="open" class="chat-widget__panel">
            <div class="chat-widget__header">
                <span>{{ title }}</span>
            </div>

            <div class="chat-widget__messages" ref="messages">
                <div
                    v-for="(msg, i) in messages"
                    :key="i"
                    class="chat-widget__msg"
                    :class="'chat-widget__msg--' + msg.role"
                >
                    <span v-html="formatText(msg.content)"></span>
                </div>
                <div v-if="loading" class="chat-widget__msg chat-widget__msg--assistant">
                    <span class="chat-widget__dots"><span>.</span><span>.</span><span>.</span></span>
                </div>
            </div>

            <div class="chat-widget__input-row">
                <input
                    v-model="input"
                    type="text"
                    :disabled="loading"
                    @keydown.enter.prevent="send"
                    class="chat-widget__input"
                />
                <button @click="send" :disabled="loading || !input.trim()" class="chat-widget__send">→</button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChatWidget',
    props: {
        title:    { type: String, default: 'Book with AI' },
        greeting: { type: String, default: 'Hi! I can help you find and book a vehicle. What dates are you looking for?' },
    },
    data() {
        return {
            open:     true,
            loading:  false,
            input:    '',
            unread:   0,
            started:  true,
            messages: [{ role: 'assistant', content: this.greeting }],
            context:  null,
        };
    },
    mounted() {
        if (window.innerWidth < 768) {
            this.open = false;
        }
    },
    methods: {
        toggle() {
            this.open = !this.open;
            if (this.open) {
                this.unread = 0;
                if (!this.started) {
                    this.started = true;
                    this.messages.push({ role: 'assistant', content: this.greeting });
                }
                this.$nextTick(() => this.scrollToBottom());
            }
        },
        async send() {
            const text = this.input.trim();
            if (!text || this.loading) return;

            this.messages.push({ role: 'user', content: text });
            this.input   = '';
            this.loading = true;
            this.$nextTick(() => this.scrollToBottom());

            try {
                // Only send user/assistant turns to the backend (skip the greeting)
                const history = this.messages
                    .filter(m => !(m.role === 'assistant' && m.content === this.greeting))
                    .filter(m => m.content);

                const payload = { messages: history };
                if (this.context) payload.context = this.context;

                const response = await axios.post('/chat/message', payload);
                this.messages.push({ role: 'assistant', content: response.data.reply });
                this.context = response.data.context || null;

                if (!this.open) this.unread++;
            } catch (e) {
                this.messages.push({
                    role: 'assistant',
                    content: 'Something went wrong. Please try again.',
                });
            } finally {
                this.loading = false;
                this.$nextTick(() => this.scrollToBottom());
            }
        },
        scrollToBottom() {
            const el = this.$refs.messages;
            if (el) el.scrollTop = el.scrollHeight;
        },
        formatText(text) {
            const escaped = String(text)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            return escaped
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline">$1</a>')
                .replace(/\n/g, '<br>');
        },
    },
};
</script>

<style scoped>
.chat-widget {
    position: fixed;
    bottom: 24px;
    left: 24px;
    z-index: 9999;
    font-family: inherit;
}

.chat-widget__bubble {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    background: #e67e22;
    color: #fff;
    border: none;
    font-size: 28px;
    cursor: pointer;
    box-shadow: 0 8px 28px rgba(230,126,34,.55);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: transform .2s, background .2s;
}
.chat-widget__bubble:hover {
    background: #cf6d17;
    transform: scale(1.08);
}

.chat-widget__badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #e74c3c;
    color: #fff;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    font-size: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chat-widget__panel {
    position: absolute;
    bottom: 88px;
    left: 0;
    width: 430px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 12px 40px rgba(0,0,0,.22);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.chat-widget__header {
    background: #e67e22;
    color: #fff;
    padding: 14px 18px;
    font-weight: 700;
    font-size: 15px;
}

.chat-widget__messages {
    flex: 1;
    height: 420px;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #f9f9f9;
}
.chat-widget__msg {
    flex-shrink: 0;
    max-width: 85%;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
    word-break: break-word;
}
.chat-widget__msg--user {
    background: #e67e22;
    color: #fff;
    align-self: flex-end;
    border-bottom-right-radius: 2px;
}
.chat-widget__msg--assistant {
    background: #fff;
    color: #222;
    align-self: flex-start;
    border-bottom-left-radius: 2px;
    box-shadow: 0 1px 4px rgba(0,0,0,.08);
}

.chat-widget__dots span {
    animation: blink 1.2s infinite;
    font-size: 18px;
}
.chat-widget__dots span:nth-child(2) { animation-delay: .2s; }
.chat-widget__dots span:nth-child(3) { animation-delay: .4s; }
@keyframes blink {
    0%, 80%, 100% { opacity: 0; }
    40%            { opacity: 1; }
}

.chat-widget__input-row {
    display: flex;
    border-top: 1px solid #eee;
    background: #fff;
}
.chat-widget__input {
    flex: 1;
    border: none;
    padding: 10px 12px;
    font-size: 13px;
    outline: none;
}
.chat-widget__send {
    background: #e67e22;
    color: #fff;
    border: none;
    padding: 0 18px;
    font-size: 18px;
    cursor: pointer;
    transition: background .2s;
}
.chat-widget__send:hover:not(:disabled) { background: #cf6d17; }
.chat-widget__send:disabled { opacity: .4; cursor: default; }

@media (max-width: 767px) {
    .chat-widget {
        bottom: 16px;
        left: 16px;
    }
    .chat-widget__bubble {
        width: 54px;
        height: 54px;
    }
    .chat-widget__bubble svg {
        width: 22px;
        height: 22px;
    }
    .chat-widget__panel {
        width: calc(100vw - 32px);
        bottom: 70px;
    }
    .chat-widget__messages {
        height: 280px;
    }
}
</style>
